import { httpClient } from 'app/api/http/http-client';
import { useMutation, useQuery } from 'react-query';

import {
  CreatePaymentRequestBody,
  Salary,
  ChangeAmountRequestBody,
  WorkersAction,
  WorkersMakePayment,
  SalaryApply,
  ConfirmPaymentFormData,
  WorkersResponse,
  SalaryHistoryParams,
  SalaryHistory,
  SalaryDetail,
  SalaryHistoryRepayment,
} from './types/salary.type';

interface WorkersParams {
  page_id: number;
  search: string;
  unlisted: boolean;
}

export const salaryService = {
  getSalaryPageData: async (): Promise<Salary> => {
    return await httpClient.get('/api-web/v1/main/payment_salary-make_payment');
  },
  getSalaryWorkers: async (params: WorkersParams): Promise<WorkersResponse> => {
    return await httpClient.get('/api-web/v1/main/payment_salary-workers', {
      params,
    });
  },
  addPerson: async (data: CreatePaymentRequestBody) => {
    return await httpClient.post('/api-web/v1/main/payment_salary-make_payment-add', data);
  },
  updatePaymentAmount: async (
    data: ChangeAmountRequestBody
  ): Promise<{
    payment_amount: number;
  }> => {
    return await httpClient.put(
      '/api-web/v1/main/payment_salary-make_payment-change_amount',
      {
        amount: data?.amount,
      },
      {
        params: {
          id: data?.id,
        },
      }
    );
  },
  clearPayment: async () => {
    return await httpClient.delete('/api-web/v1/main/payment_salary-make_payment-clear');
  },
  deletePayment: async (id: string) => {
    return await httpClient.delete(`/api-web/v1/main/payment_salary-make_payment-delete?id=${id}`);
  },
  uploadFile: async (file: File) => {
    const formData = new FormData();
    formData.append('inputfile', file);

    return await httpClient.post('/api-web/v1/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Accept: '*/*',
      },
    });
  },
  uploadFileName: async (fileName: string): Promise<Salary> => {
    return await httpClient.post(`/api-web/v1/main/payment_salary-make_payment-upload`, {
      file_name: fileName,
    });
  },
  changeWorkersStatus: async (data: WorkersAction) => {
    return await httpClient.put('/api-web/v1/main/payment_salary-workers-action', data);
  },
  sendWorkersToPayment: async (data: WorkersMakePayment) => {
    return await httpClient.put('/api-web/v1/main/payment_salary-workers-make_payment', data);
  },
  sendSalaryApply: async (data: SalaryApply): Promise<ConfirmPaymentFormData> => {
    return await httpClient.post('/api-web/v1/main/payment_salary-make_payment-apply', data);
  },
  sendtoSign: async () => {
    return await httpClient.put('/api-web/v1/main/payment_salary-make_payment-confirm', {
      form_id: 'save_to_sign',
    });
  },
  getSalaryHistory: async (params: SalaryHistoryParams): Promise<SalaryHistory> => {
    return await httpClient.get('/api-web/v1/main/payment_salary-history', {
      params,
    });
  },
  sendToDirectSign: async (): Promise<any> => {
    return await httpClient.put('/api-web/v1/main/payment_salary-make_payment-confirm', {
      form_id: 'sign',
    });
  },
  getSalaryHistoryDetail: async (trn_id: string): Promise<SalaryDetail> => {
    return await httpClient.get(`/api-web/v1/main/payment_salary-history-details?trn_id=${trn_id}&page_id=1`);
  },

  getSalaryHistoryFile: async (trn_id: string) => {
    return await httpClient.post(`/api-web/v1/main/transfer-details`, {
      action: 'print_form',
      trn_id,
    });
  },
  updateSalaryHistoryRepayment: async (data: SalaryHistoryRepayment) => {
    return await httpClient.put('/api-web/v1/main/payment_salary-history-repayment', data);
  },
};

export const useGetSalaryData = () => {
  return useQuery('getSalaryData', salaryService.getSalaryPageData);
};

export const useGetSalaryHistoryDetail = (trn_id: string, enabled: boolean) => {
  return useQuery(['getSalaryHistoryDetail', trn_id], () => salaryService.getSalaryHistoryDetail(trn_id), {
    enabled: !!trn_id && enabled,
  });
};

export const useGetSalaryWorkers = (params: WorkersParams, enabled: boolean) => {
  const { data, isLoading, refetch, isFetching } = useQuery(
    ['getSalaryWorkers', params],
    () => salaryService.getSalaryWorkers(params),
    {
      enabled,
    }
  );

  return {
    data,
    isLoading,
    fetchingWorkers: isFetching,
    refetchWorkers: refetch,
  };
};

export const useGetSalaryHistory = (params: SalaryHistoryParams) => {
  return useQuery(['getSalaryHistory', params], () => salaryService.getSalaryHistory(params));
};

export const useAddPerson = () => {
  return useMutation(salaryService.addPerson);
};

export const useUpdatePaymentAmount = () => {
  return useMutation(salaryService.updatePaymentAmount);
};

export const useClearPaymentList = () => {
  return useMutation(salaryService.clearPayment);
};

export const useDeletePayment = () => {
  return useMutation(salaryService.deletePayment);
};

export const useUploadFile = () => {
  return useMutation(salaryService.uploadFile);
};

export const useUploadFileName = () => {
  return useMutation(salaryService.uploadFileName);
};

export const useChangeWorkersStatus = () => {
  return useMutation(salaryService.changeWorkersStatus);
};

export const useSendWorkersToPayment = () => {
  return useMutation(salaryService.sendWorkersToPayment);
};

export const useSendSalaryApply = () => {
  return useMutation(salaryService.sendSalaryApply);
};

export const useSendSalaryToSign = () => {
  return useMutation(salaryService.sendtoSign);
};

export const useSendSalaryToDirectSign = () => {
  return useMutation(salaryService.sendToDirectSign);
};

export const useGetSalaryHistoryFile = () => {
  return useMutation(salaryService.getSalaryHistoryFile);
};

export const useUpdateSalaryHistoryRepayment = () => {
  return useMutation(salaryService.updateSalaryHistoryRepayment);
};
